<script setup lang="ts">
  import { useEventListener } from '@vueuse/core'
  import { Money3Component as InputMoney } from 'v-money3'
  import { computed, nextTick, onMounted, useTemplateRef } from 'vue'
  import { randomId } from '@/functions'

  const props = defineProps<{
    id?: string,
    modelValue: number,
    max?: number,
    min?: number,
    label?: string,
    required?: boolean,
    disabled?: boolean,
    readonly?: boolean,
    placeholder?: string,
    error?: string[],
    prefix?: string,
    suffix?: string,
    plusMinusButtons?: boolean,
    incrementStep?: number,
    maxBy?:
      | 'tinyint'
      | 'utinyint'
      | 'smallint'
      | 'usmallint'
      | 'mediumint'
      | 'umediumint'
      | 'int'
      | 'uint'
      | 'bigint',
  }>()

  const emit = defineEmits<{
    (e: 'update:modelValue', value: number): void,
  }>()

  // https://github.com/jonathanpmartins/v-money3#properties
  const options = computed(() => ({
    decimal: ',',
    thousands: '.',
    precision: 0,
    min: props.min ?? 0,
    max: limitMax.value,
  }))

  const limitMax = computed(() => {
    if (props.max) {
      return props.max
    }

    const maxBy = props.maxBy ?? 'uint'
    switch (maxBy) {
      case 'tinyint': return 100 // 127
      case 'utinyint': return 255 // 255
      case 'smallint': return 32_000 // 32,767
      case 'usmallint': return 65_000 // 65,535
      case 'mediumint': return 8_000_000 // 8,388,607
      case 'umediumint': return 16_000_000 // 16,777,215
      case 'int': return 2_000_000_000 // 2,147,483,647
      case 'uint': return 4_000_000_000 // 4,294,967,295
      case 'bigint': return Number.MAX_SAFE_INTEGER
      default: return 4_000_000_000 // same as uint
    }
  })

  const textInput = computed({
    set: (val: number | string) => {
      emit('update:modelValue', Number(val))
      nextTick(() => {
        // enforce max / min limit
        if (Number(val) > limitMax.value) {
          emit('update:modelValue', limitMax.value)
        }
        if (Number(val) < options.value.min) {
          emit('update:modelValue', options.value.min)
        }
      })
    },
    get: () => props.modelValue,
  })

  const slotContainer = useTemplateRef('slotContainer')
  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowUp') {
      textInput.value += props.incrementStep ?? 1
      event.preventDefault()
    } else if (event.key === 'ArrowDown') {
      textInput.value -= props.incrementStep ?? 1
      event.preventDefault()
    }
  }
  onMounted(() => {
    const inputElement = slotContainer.value?.querySelector('input')
    if (inputElement) {
      useEventListener(inputElement, 'keydown', handleKeydown)
    }
  })

  const defaultId = randomId()
  const inputId = computed(() => props.id ?? defaultId)
</script>

<template>
  <div ref="slotContainer">
    <label
      v-if="label"
      :for="inputId"
      class="form-label">
      {{ label }}
      <span
        v-if="required"
        class="text-red-600">
        *
      </span>
    </label>
    <div
      :class="{
        'flex': plusMinusButtons,
        'contents': !plusMinusButtons, // no extra div if not using plusMinusButtons
      }">
      <app-button
        v-if="plusMinusButtons"
        :disabled="disabled"
        class="h-9 w-7 px-0 btn-gray-outline rounded-r-none border-r-0"
        @click="textInput--">
        -
      </app-button>
      <div class="relative flex items-center">
        <input-money
          v-bind="options"
          :id="inputId"
          v-model="textInput"
          :readonly="readonly"
          :disabled="disabled"
          class="form-input"
          :class="{'rounded-none! text-center w-10! px-0!': plusMinusButtons}" />
        <span
          v-if="prefix"
          class="prefix">
          {{ prefix }}
        </span>
        <span
          v-if="suffix"
          class="suffix">
          {{ suffix }}
        </span>
      </div>
      <app-button
        v-if="plusMinusButtons"
        :disabled="disabled"
        class="h-9 w-7 px-0 btn-gray-outline rounded-l-none border-l-0"
        @click="textInput++">
        +
      </app-button>
    </div>
    <app-error-msg :msg="error" />
  </div>
</template>

<style scoped>
  @reference "@/assets/styles/main.css";

  .prefix,
  .suffix {
    @apply absolute text-gray-400 pointer-events-none text-sm;
  }
  .prefix {
    @apply left-4;
  }
  .suffix {
    @apply right-4;
  }
  .input-has-text .prefix.suffix {
    @apply inline;
  }

  .form-input:has(+ span.prefix) {
    @apply pl-10;
  }
  .form-input:has(+ span.suffix) {
    @apply pr-10;
  }

  .text-right .form-input {
    @apply text-right;
  }
  .text-center .form-input {
    @apply text-center;
  }
  .rounded-none .form-input {
    @apply rounded-none;
  }
</style>
